import React, { createContext, useCallback, useContext, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { useQuery } from '@tanstack/react-query'
import {
  useGridPageState,
  useGridSortState
} from '../../../DenaliDataPipeline/report/HierarchicalOpalGrid'
import { useUserLoginActivity } from '../../../../../api/users/loginActivity'
import { useAppContext } from '../../../../../redux/slices/appContext'
import { fetchClients } from '../../../../../service'
import { DEFAULT_PAGE_SIZE, DEFAULT_GRID_SORT, HOUSEHOLDS_QUERY_KEY } from './constants'
import {
  applyDateRangeToQuery,
  buildLoginActivityFilters,
  INITIAL_FILTER_STATE,
  mapSortState,
  normalizeGridSortState,
  toGridSortState
} from './queryBuilders'

const UserLoginActivityContext = createContext(null)

export function useUserLoginActivityContext () {
  const ctx = useContext(UserLoginActivityContext)
  if (!ctx) {
    throw new Error('useUserLoginActivityContext must be used within UserLoginActivityProvider')
  }
  return ctx
}

export default function UserLoginActivityProvider ({ config, children }) {
  const { userProfile, userId } = useAppContext()
  const allAccess = !!userProfile?.allAccountsAccess
  const pageSize = config?.defaultPageSize ?? DEFAULT_PAGE_SIZE

  const { pageState, setPageState } = useGridPageState(pageSize)
  const { sortState, setSortState: setSortStateRaw } = useGridSortState(DEFAULT_GRID_SORT)
  const [filterState, setFilterState] = useState(INITIAL_FILTER_STATE)

  const { data: householdClients = [], isLoading: householdsLoading } = useQuery({
    queryKey: [HOUSEHOLDS_QUERY_KEY, userId],
    queryFn: async () => {
      const { data } = await fetchClients()
      return Array.isArray(data) ? data : []
    }
  })

  const householdOptions = useMemo(
    () => householdClients.map(({ clientId, shortName }) => ({
      value: clientId,
      label: shortName
    })),
    [householdClients]
  )

  const query = useMemo(() => {
    const base = applyDateRangeToQuery({
      skip: pageState.pageIndex * pageState.pageSize,
      take: pageState.pageSize,
      sort: mapSortState(sortState)
    }, filterState)
    const filters = buildLoginActivityFilters(filterState)
    if (Object.keys(filters).length) {
      base.filters = filters
    }
    return base
  }, [pageState.pageIndex, pageState.pageSize, sortState, filterState])

  const { rows, total, isLoading, isFetching } = useUserLoginActivity({
    query,
    allAccess
  })

  const resetPage = useCallback(() => {
    setPageState((prev) => ({ ...prev, pageIndex: 0 }))
  }, [setPageState])

  const setNeverLoggedIn = useCallback((checked) => {
    setFilterState((prev) => ({ ...prev, neverLoggedIn: !!checked }))
    resetPage()
  }, [resetPage])

  const commitSelectedHouseholds = useCallback((selectedHouseholdClientIds) => {
    setFilterState((prev) => ({
      ...prev,
      selectedHouseholdClientIds: Array.isArray(selectedHouseholdClientIds)
        ? selectedHouseholdClientIds
        : []
    }))
    resetPage()
  }, [resetPage])

  const commitDateRange = useCallback(({ preset, startDate, endDate }) => {
    setFilterState((prev) => ({
      ...prev,
      dateRangePreset: preset ?? null,
      startDate: startDate ?? '',
      endDate: endDate ?? ''
    }))
    resetPage()
  }, [resetPage])

  const setSearchText = useCallback((term) => {
    const next = term?.trim() ?? ''
    setFilterState((prev) => ({
      ...prev,
      searchText: next
    }))
    resetPage()
  }, [resetPage])

  const handleSortChange = useCallback((updater) => {
    setSortStateRaw((prev) => {
      const gridPrev = toGridSortState(prev)
      const next = typeof updater === 'function' ? updater(gridPrev) : updater
      return normalizeGridSortState(next)
    })
    resetPage()
  }, [setSortStateRaw, resetPage])

  const handlePageChange = useCallback((updater) => {
    setPageState(updater)
  }, [setPageState])

  const ctx = useMemo(() => ({
    config,
    allAccess,
    query,
    filterState,
    pageState,
    sortState,
    rows,
    total,
    isLoading,
    isFetching,
    onPageChange: handlePageChange,
    onSortChange: handleSortChange,
    householdOptions,
    selectedHouseholdClientIds: filterState.selectedHouseholdClientIds,
    householdsLoading,
    dateRange: {
      preset: filterState.dateRangePreset,
      startDate: filterState.startDate,
      endDate: filterState.endDate
    },
    setNeverLoggedIn,
    commitSelectedHouseholds,
    commitDateRange,
    searchText: filterState.searchText,
    setSearchText
  }), [
    config,
    allAccess,
    query,
    filterState,
    pageState,
    sortState,
    rows,
    total,
    isLoading,
    isFetching,
    handlePageChange,
    handleSortChange,
    householdOptions,
    householdsLoading,
    setNeverLoggedIn,
    commitSelectedHouseholds,
    commitDateRange,
    setSearchText
  ])

  return (
    <UserLoginActivityContext.Provider value={ctx}>
      {children}
    </UserLoginActivityContext.Provider>
  )
}

UserLoginActivityProvider.propTypes = {
  config: PropTypes.object,
  children: PropTypes.node
}
